import { useCallback, useState } from 'react';
import type { ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import { MessageBanner } from '@library/components/banners/MessageBanner';
import { SelectPopoverContainer } from '@library/components/selects/SelectPopoverContainer';

interface BulkButtonProps {
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'> | false
  ) => void;
  label: string;
  confirmationLabel: string;
  buttonText: string;
  SelectComponent: React.ComponentType<{
    value: string;
    onChange: (value: string) => void;
    label: string;
    className?: string;
    containerClassName?: string;
  }>;
  enabledText?: string;
  disabledText?: string;
  handleConfirm: (isEnabled: boolean) => void;
  isLoading: boolean;
  isError: boolean;
}

export const BulkButton = ({
  setConfirmationModalProps,
  label,
  confirmationLabel,
  buttonText,
  SelectComponent,
  enabledText = 'Enable',
  disabledText = 'Disable',
  handleConfirm,
  isLoading,
  isError
}: BulkButtonProps) => {
  const [isEnabled, setIsEnabled] = useState(true);

  const handleSave = useCallback(() => {
    setConfirmationModalProps({
      confirmationCopy: 'Change',
      handleConfirm: () => handleConfirm(isEnabled),
      label: confirmationLabel,
      children: `Are you sure you want to ${isEnabled ? 'enable' : 'disable'} ${label.toLowerCase()} for all projections that match your search criteria?`,
      isOpen: true,
      isLoading
    });
  }, [
    setConfirmationModalProps,
    handleConfirm,
    confirmationLabel,
    isEnabled,
    isLoading,
    label
  ]);

  const handleChange = useCallback(
    (newStatus: string) => {
      setIsEnabled(newStatus !== disabledText);
    },
    [disabledText]
  );

  return (
    <SelectPopoverContainer buttonText={buttonText} handleOnClick={handleSave}>
      <SelectComponent
        containerClassName="flex"
        className="items-center justify-self-center px-2"
        label={label}
        onChange={handleChange}
        value={isEnabled ? enabledText : disabledText}
      />
      {isError && (
        <MessageBanner code="ERROR" message="Error updating projections" />
      )}
    </SelectPopoverContainer>
  );
};
